<div class="content error-config-page">
  <div class="container-fluid">
    <div class="error-config-header">
      <div>
        <h1>Error Configuration</h1>
        <p>Manage client Error Types and Error Categories used to pre-map the audit questionnaire.</p>
      </div>
      <div class="error-config-actions" *ngIf="canEdit">
        <button *ngIf="activeTab === 'types'" type="button" class="btn btn-primary" (click)="openTypeCreate()">
          <i class="fa-solid fa-plus mr-1"></i> Add Error Type
        </button>
        <button *ngIf="activeTab === 'categories'" type="button" class="btn btn-primary" (click)="openCategoryCreate()">
          <i class="fa-solid fa-plus mr-1"></i> Add Error Category
        </button>
      </div>
    </div>

    <div class="error-config-metrics">
      <div class="error-metric-card">
        <span>Error Types</span>
        <strong>{{ errorTypes.length }}</strong>
        <small>{{ activeTypeCount }} active</small>
      </div>
      <div class="error-metric-card">
        <span>Error Categories</span>
        <strong>{{ errorCategories.length }}</strong>
        <small>{{ activeCategoryCount }} active</small>
      </div>
    </div>

    <div class="error-config-card">
      <div class="error-config-tabs">
        <button type="button" [class.active]="activeTab === 'types'" (click)="selectTab('types')">Error Types</button>
        <button type="button" [class.active]="activeTab === 'categories'" (click)="selectTab('categories')">Error Categories</button>
      </div>

      <div class="error-loading" *ngIf="loading">
        <span class="spinner-border spinner-border-sm mr-2"></span>
        Loading error configuration...
      </div>

      <div class="table-responsive" *ngIf="!loading && activeTab === 'types'">
        <table class="table table-sm error-config-table">
          <thead>
            <tr><th>Error Type</th><th>Status</th><th *ngIf="canEdit">Action</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of errorTypes; trackBy: trackById" [class.config-inactive]="!item.active">
              <td><strong>{{ item.name }}</strong></td>
              <td><span class="status-badge" [class.status-active]="item.active" [class.status-inactive]="!item.active">{{ item.active ? 'Active' : 'Inactive' }}</span></td>
              <td *ngIf="canEdit">
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="openTypeEdit(item)">
                  <i class="fa-solid fa-pen mr-1"></i> Edit
                </button>
              </td>
            </tr>
            <tr *ngIf="!errorTypes.length"><td [attr.colspan]="canEdit ? 3 : 2" class="empty-row">No Error Types configured.</td></tr>
          </tbody>
        </table>
      </div>

      <div class="table-responsive" *ngIf="!loading && activeTab === 'categories'">
        <table class="table table-sm error-config-table">
          <thead>
            <tr><th>Error Category</th><th>Default Error Type</th><th>Status</th><th *ngIf="canEdit">Action</th></tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of errorCategories; trackBy: trackById" [class.config-inactive]="!item.active">
              <td><strong>{{ item.name }}</strong></td>
              <td>{{ item.defaultErrorTypeName || 'No default' }}</td>
              <td><span class="status-badge" [class.status-active]="item.active" [class.status-inactive]="!item.active">{{ item.active ? 'Active' : 'Inactive' }}</span></td>
              <td *ngIf="canEdit">
                <button type="button" class="btn btn-sm btn-outline-primary" (click)="openCategoryEdit(item)">
                  <i class="fa-solid fa-pen mr-1"></i> Edit
                </button>
              </td>
            </tr>
            <tr *ngIf="!errorCategories.length"><td [attr.colspan]="canEdit ? 4 : 3" class="empty-row">No Error Categories configured.</td></tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="config-modal-backdrop" *ngIf="typeEditorOpen">
      <div class="config-modal">
        <div class="config-modal-header">
          <div><h3>{{ typeEditor.id === null ? 'Add Error Type' : 'Edit Error Type' }}</h3><p>Error Types are frozen into audit revisions through questionnaire mappings.</p></div>
          <button type="button" class="config-close" (click)="closeEditors()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="config-modal-body">
          <label>Error Type Name <span>*</span></label>
          <input type="text" [(ngModel)]="typeEditor.name" maxlength="100" placeholder="e.g. BENEFIT LEVEL">
          <div class="config-active-row" *ngIf="typeEditor.id !== null">
            <div><strong>Active</strong><small>Inactive values remain historical but cannot be used for new mappings.</small></div>
            <input type="checkbox" [(ngModel)]="typeEditor.active">
          </div>
        </div>
        <div class="config-modal-footer">
          <button type="button" class="btn btn-outline-secondary" (click)="closeEditors()" [disabled]="saving">Cancel</button>
          <button type="button" class="btn btn-primary" (click)="saveType()" [disabled]="saving">
            <span *ngIf="saving" class="spinner-border spinner-border-sm mr-1"></span>
            {{ typeEditor.id === null ? 'Create Error Type' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>

    <div class="config-modal-backdrop" *ngIf="categoryEditorOpen">
      <div class="config-modal">
        <div class="config-modal-header">
          <div><h3>{{ categoryEditor.id === null ? 'Add Error Category' : 'Edit Error Category' }}</h3><p>Optionally force a default Error Type for every questionnaire mapping using this category.</p></div>
          <button type="button" class="config-close" (click)="closeEditors()"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <div class="config-modal-body">
          <label>Error Category Name <span>*</span></label>
          <input type="text" [(ngModel)]="categoryEditor.name" maxlength="100" placeholder="e.g. DEMOGRAPHIC / REFERENCE">

          <label>Default Error Type</label>
          <select [(ngModel)]="categoryEditor.defaultErrorTypeId">
            <option [ngValue]="null">No default</option>
            <option *ngFor="let item of activeErrorTypes" [ngValue]="item.id">{{ item.name }}</option>
          </select>
          <small>When a default is selected, Questionnaire Configuration automatically uses and locks that Error Type.</small>

          <div class="config-active-row" *ngIf="categoryEditor.id !== null">
            <div><strong>Active</strong><small>Inactive values remain historical but cannot be used for new mappings.</small></div>
            <input type="checkbox" [(ngModel)]="categoryEditor.active">
          </div>
        </div>
        <div class="config-modal-footer">
          <button type="button" class="btn btn-outline-secondary" (click)="closeEditors()" [disabled]="saving">Cancel</button>
          <button type="button" class="btn btn-primary" (click)="saveCategory()" [disabled]="saving">
            <span *ngIf="saving" class="spinner-border spinner-border-sm mr-1"></span>
            {{ categoryEditor.id === null ? 'Create Error Category' : 'Save Changes' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
